import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Dropdown, Space } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Role, RoleType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Caption01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { RoleOption } from './types';

const MenuItem = styled(Menu.Item)`
  justify-content: space-between;
`;
const Count = styled.span`
  ${Caption01};
  color: var(--gray-50);
`;
const StyledOutsideClickHandler = styled(OutsideClickHandler)`
  display: inline-flex;
`;

interface Props {
  roleOptions: RoleOption[];
  numberOfEntities: number;
  choosenRoles: Role[];
  onRoleSelectionChange: (updated: Role[]) => void;
}
export const RoleSelectionDropdown: FC<Props> = ({
  roleOptions,
  numberOfEntities,
  choosenRoles,
  onRoleSelectionChange,
}): JSX.Element => {
  const allText = useTranslation('all');
  const defaultOption = useMemo(
    () => ({
      roleName: allText,
      roleId: -1,
      numberOfEntities,
      type: RoleType.Vendor,
    }),
    [allText, numberOfEntities],
  );
  const options = useMemo(
    () => [defaultOption].concat(roleOptions),
    [defaultOption, roleOptions],
  );
  const choosenRoleIds = useMemo(
    () => choosenRoles.map(({ roleId }) => roleId.toString()),
    [choosenRoles],
  );
  const hasMore = choosenRoles.length > 2;
  const [visible, setVisible] = useState(false);
  const onSelect = (option: RoleOption) => {
    if (option.roleId === defaultOption.roleId) {
      onRoleSelectionChange([]);
    } else if (choosenRoleIds.includes(option.roleId.toString())) {
      onRoleSelectionChange(
        choosenRoles.filter(({ roleId }) => roleId !== option.roleId),
      );
    } else {
      onRoleSelectionChange([...choosenRoles, option]);
    }
  };
  const isChild = isChildNode([
    'filter-box',
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
  ]);

  return (
    <StyledOutsideClickHandler
      allowed={isChild}
      onOutsideClick={() => setVisible(false)}
    >
      <Dropdown
        overlay={
          <Menu
            selectedKeys={
              choosenRoleIds.length > 0
                ? choosenRoleIds
                : [defaultOption.roleId.toString()]
            }
            style={{ height: '280px', overflow: 'auto' }}
          >
            <Menu.Header>{useTranslation('role')}</Menu.Header>
            {options.map((option) => (
              <MenuItem key={option.roleId} onClick={() => onSelect(option)}>
                <span>{option.roleName}</span>
                <Count>{option.numberOfEntities}</Count>
              </MenuItem>
            ))}
          </Menu>
        }
        trigger={['click']}
        visible={visible}
      >
        <Button
          type="text"
          size="small"
          icon={<ChevronDown16 />}
          iconPlacement="right"
          onClick={() => setVisible((prev) => !prev)}
        >
          <Space size={4}>
            <div>{useTranslation('role')}:</div>
            <>
              {choosenRoles.length > 0 ? (
                <strong>
                  {choosenRoles
                    .slice(0, 2)
                    .map((role) => role.roleName)
                    .join(', ')}
                  {hasMore ? (
                    <FormattedMessage
                      id="resource_management.roles.plus_more"
                      values={{ count: choosenRoles.length - 2 }}
                    />
                  ) : null}
                </strong>
              ) : (
                <strong>{defaultOption.roleName}</strong>
              )}
            </>
          </Space>
        </Button>
      </Dropdown>
    </StyledOutsideClickHandler>
  );
};
